/**
 * @file PhotoFocusField.tsx
 * @description Точка фокуса фото фона для ПК и телефона (phase19.16, партия
 * SB120, этап 1; uiux-sb120-stage1 §2.3 и «Дополнение» §2.1; данные
 * db-design-sb120-stage1 §1 `focus` / `focusPhone`):
 *
 * ```
 * Точка фокуса     [ ПК | Телефон ]          ↺ как на ПК (у телефона)
 * ┌──────────────────────────────┐   ПК — рамка по ширине × высоте блока,
 * │  ░░░░┌───────┐░░░░░░░░░░░░░░ │   телефон — 360 × высота телефона;
 * │  ░░░░│   ⊕   │░░░░            │   ░ — уйдёт за край на этом экране
 * └──────────────────────────────┘
 * ```
 *
 * Телефон: своё фото (`mediaIdPhone`) — точка ставится на нём; нет своей
 * точки — как на ПК. При высоте «По фото» фото не обрезается — точка нужна,
 * только если текст не поместится.
 */

import type { SectionLook, SectionMediaBg } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { FieldHint } from "../../../ui/settings-groups";
import { DeviceTabs } from "../fields/DeviceTabs";
import { FocalPointPicker } from "../fields/FocalPointPicker";
import { useSectionForm } from "../section-form-context";
import { mediaAspect } from "../../../../hooks/site-builder/useMediaSizes";
import { frameAspect, type Device } from "../../../../lib/site-builder/look-size";

interface Props {
  look: SectionLook;
  bg: SectionMediaBg;
  onChange: (bg: SectionMediaBg) => void;
}

export function PhotoFocusField({ look, bg, onChange }: Props) {
  const { mediaSizes } = useSectionForm();
  const [device, setDevice] = useState<Device>("desktop");
  const phone = device === "phone";
  const mediaId = phone ? (bg.mediaIdPhone ?? bg.mediaId) : bg.mediaId;
  const focus = phone ? (bg.focusPhone ?? bg.focus) : bg.focus;
  const zoom = phone ? (bg.zoomPhone ?? bg.zoom) : bg.zoom;
  const photoAspect = mediaAspect(mediaSizes, mediaId);
  const height = phone ? (look.heightPhone ?? look.height) : look.height;
  const whole = height.kind === "photo" && height.aspect === undefined;

  function resetPhone() {
    const next = { ...bg };
    delete next.focusPhone;
    onChange(next);
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <Label>Точка фокуса</Label>
        <DeviceTabs value={device} onChange={setDevice} label="Точка фокуса: экран" phoneOwn={bg.focusPhone !== undefined} />
      </div>
      {phone ? (
        bg.focusPhone ? (
          <Button variant="ghost" size="sm" className="h-12 self-start can-hover:h-8" onClick={resetPhone} aria-label="Точка фокуса на телефоне: как на ПК">
            <RotateCcw aria-hidden />
            как на ПК
          </Button>
        ) : (
          <span className="type-caption text-on-surface-variant">Сейчас как на ПК — нажмите на фото, чтобы поставить свою.</span>
        )
      ) : null}
      <FocalPointPicker
        mediaId={mediaId}
        value={focus}
        frame={{ aspect: frameAspect(look, device, photoAspect), photoAspect, zoom }}
        onChange={(next) => onChange(phone ? { ...bg, focusPhone: next } : { ...bg, focus: next })}
      />
      {whole ? <FieldHint className="mt-0">Высота «По фото» — фото видно целиком. Точка нужна, только если текст не поместится.</FieldHint> : null}
    </div>
  );
}
